/**
 * Derive the centre-pane's open file-tab set, reconciled against the authoritative
 * artifacts list. The open set is the union of manually-Viewed files (`?files=`,
 * in order) and the workstream's ledger files (auto-opened), MINUS:
 *   - files the user closed this session (`closedIds`), and
 *   - files reconciled GONE.
 *
 * A file is GONE when the list has loaded (`existingIds !== null`) and no longer
 * contains it, provided it was **previously listed** (`everListedIds` — a real
 * delete / eviction) OR it has **no ledger backing** (a stale or hand-crafted
 * `?files=` id). A fresh ledger deliverable that has not listed yet is kept.
 *
 * "Gone" is a pure derivation, never a stored close — so a transient list blip
 * recovers the moment the list includes the file again, while a real delete stays
 * out because the list keeps excluding it.
 */
export function deriveOpenFileIds(params: {
  /** Manually-Viewed ids (`?files=`), in tab order. */
  readonly manualIds: readonly string[];
  /** The workstream ledger's artifactIds (auto-opened). */
  readonly ledgerIds: readonly string[];
  /** Ids the user closed this session. */
  readonly closedIds: ReadonlySet<string>;
  /** The current artifacts-list ids, or `null` when the list has not loaded. */
  readonly existingIds: ReadonlySet<string> | null;
  /** Every id ever seen in the artifacts list (distinguishes "gone" from "not listed yet"). */
  readonly everListedIds: ReadonlySet<string>;
}): string[] {
  const { manualIds, ledgerIds, closedIds, existingIds, everListedIds } = params;
  const ledgerSet = new Set(ledgerIds);
  const isGone = (id: string): boolean =>
    existingIds !== null && !existingIds.has(id) && (everListedIds.has(id) || !ledgerSet.has(id));
  return [...new Set([...manualIds, ...ledgerIds])].filter(
    (id) => !closedIds.has(id) && !isGone(id),
  );
}
